import { html } from 'lit';
import '@spectrum-web-components/table/sp-table-row.js';
import '@spectrum-web-components/table/sp-table-cell.js';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/combobox/sp-combobox.js';
import type { TaskInteractionResponse } from '../../../api/types';
import type { WorkflowUserInputRequest } from '../dialogs/types.js';
import {
  buildWorkflowUserInputSubmission,
  getWorkflowUserInputControls,
} from './user-input-controls.js';

function formatPreview(value: unknown, emptyText?: string): string {
  if (typeof value === 'string') return value;
  if (value === undefined) return emptyText ?? 'No input data.';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export type OverviewInteractionActionDetail = {
  workflowId: number;
  taskId: number;
  response: TaskInteractionResponse;
  file?: File;
};

function actionClassName(
  name: string,
  label: string,
  variant?: string
): string {
  const normalizedName = name.toLowerCase().replace(/[^a-z0-9_-]/g, '-');
  const normalizedVariant = variant === 'secondary' ? 'secondary' : 'primary';
  const normalizedLabel = label.toLowerCase();
  const semanticClass =
    normalizedLabel === 'approve'
      ? ' user-input-action-approve'
      : normalizedLabel === 'reject'
        ? ' user-input-action-reject'
        : '';
  return `user-input-action user-input-action-${normalizedName} user-input-action-${normalizedVariant}${semanticClass}`;
}

export function renderWorkflowOverviewUserInputActions(args: {
  renderRoot: ParentNode;
  workflowId: number;
  request: WorkflowUserInputRequest;
  processing: boolean;
  processingAction?: string;
  emitInteraction: (detail: OverviewInteractionActionDetail) => void;
}) {
  const {
    renderRoot,
    workflowId,
    request,
    processing,
    processingAction,
    emitInteraction,
  } = args;
  const { controls, buttons } = getWorkflowUserInputControls(request);
  const hasSelectControl = controls.some(
    (control) => control.type === 'select'
  );
  const selectableOptionCount = controls
    .filter((control) => control.type === 'select')
    .reduce((count, control) => count + control.options.length, 0);
  const hasFileControl = controls.some((control) => control.type === 'file');

  return buttons.map(
    (button) => html`<sp-action-button
      class=${actionClassName(button.name, button.label, button.variant)}
      ?disabled=${
        processing ||
        (hasSelectControl && selectableOptionCount === 0 && !hasFileControl)
      }
      @click=${() => {
        const submission = buildWorkflowUserInputSubmission({
          request,
          renderRoot,
          actionName: button.name,
          controlIdPrefix: `${request.taskId}:`,
        });
        if (!submission) return;

        emitInteraction({
          workflowId,
          taskId: request.taskId,
          response: submission.response,
          file: submission.file,
        });
      }}
    >
      ${
        processing && processingAction === button.name
          ? `${button.label}…`
          : button.label
      }
    </sp-action-button>`
  );
}

export function renderWorkflowOverviewUserInputPanel(args: {
  renderRoot: ParentNode;
  workflowId: number;
  request: WorkflowUserInputRequest;
  processing: boolean;
  processingAction?: string;
  emitInteraction: (detail: OverviewInteractionActionDetail) => void;
  hideActions?: boolean;
}) {
  const {
    renderRoot,
    workflowId,
    request,
    processing,
    processingAction,
    emitInteraction,
    hideActions = false,
  } = args;
  const interaction = request.interaction;
  const configData = (request.config ?? {}) as Record<string, unknown>;
  const title =
    (typeof configData.title === 'string' && configData.title.trim()) ||
    interaction?.title ||
    'User input required';
  const message =
    (typeof configData.message === 'string' && configData.message.trim()) ||
    interaction?.message ||
    'This workflow is waiting for user input.';

  const { controls } = getWorkflowUserInputControls(request);

  return html`
    <div class="user-input-wrap">
      <div class="user-input-header">
        <h3 class="user-input-title">${title}</h3>
      </div>
      ${message ? html`<p class="user-input-message">${message}</p>` : null}
      ${controls.map((control) => {
        if (control.type === 'select') {
          return html`<sp-combobox
            data-control-id="${request.taskId}:${control.controlId}"
            label=${control.label}
            .value=${control.value}
            .options=${control.options}
            ?disabled=${processing || control.options.length === 0}
          ></sp-combobox>`;
        }
        if (control.type === 'file') {
          return html`<input
            data-control-id="${request.taskId}:${control.controlId}"
            type="file"
            aria-label=${control.label}
            accept=${control.accept ?? ''}
            ?disabled=${processing}
          />`;
        }
        return html`<pre class="input-preview">${formatPreview(control.value, control.emptyText)}</pre>`;
      })}
      ${
        hideActions
          ? null
          : html`<div class="actions">
            ${renderWorkflowOverviewUserInputActions({
              renderRoot,
              workflowId,
              request,
              processing,
              processingAction,
              emitInteraction,
            })}
          </div>`
      }
    </div>
  `;
}

export function renderWorkflowOverviewUserInputRow(args: {
  renderRoot: ParentNode;
  workflowId: number;
  request: WorkflowUserInputRequest;
  processing: boolean;
  processingAction?: string;
  emitInteraction: (detail: OverviewInteractionActionDetail) => void;
}) {
  const {
    renderRoot,
    workflowId,
    request,
    processing,
    processingAction,
    emitInteraction,
  } = args;

  return html`
    <sp-table-row class="row">
      <sp-table-cell colspan=${7}>
        ${renderWorkflowOverviewUserInputPanel({
          renderRoot,
          workflowId,
          request,
          processing,
          processingAction,
          emitInteraction,
        })}
      </sp-table-cell>
    </sp-table-row>
  `;
}
